<script lang="ts">
  import PubSub from 'pubsub-js'
  import { onMount } from 'svelte'
  import ImagePreviewModal from '@/components/appDriver/ImagePreviewModal.svelte'
  import Navbar from '@/components/appDriver/Navbar.svelte'
  import { openDeeplink } from '@/plugins/util'
  import { currentPage } from '@/components/appDriver/store'
  import AnnouncementBadges from '@/components/appDriver/newsfeed/components/AnnouncementBadges.svelte'
  import NewsComingSoonState from '@/components/appDriver/newsfeed/components/NewsComingSoonState.svelte'
  import NewsEmptyState from '@/components/appDriver/newsfeed/components/NewsEmptyState.svelte'
  import { isNewsfeedFeatureEnabled } from '@/components/appDriver/newsfeed/feature'
  import PageNewsList from '@/components/appDriver/newsfeed/pages/PageNewsList.svelte'
  import { getNewsPostDetail } from '@/components/appDriver/newsfeed/service'
  import { markNewsPostAsRead, selectedNewsPost, selectedNewsPostId, setSelectedNewsPost } from '@/components/appDriver/newsfeed/store'
  import type { NewsPost } from '@/components/appDriver/newsfeed/types'
  import { formatNewsDate, getNewsContentParagraphs } from '@/components/appDriver/newsfeed/utils'

  let isLoading = true
  let post: NewsPost | null = null
  let showComingSoonState = !isNewsfeedFeatureEnabled
  let previewImageUrl = ''
  let previewImageAlt = ''

  onMount(() => {
    $currentPage = 'news-detail'

    if (!isNewsfeedFeatureEnabled) {
      showComingSoonState = true
      isLoading = false
      post = null
      return
    }

    loadNewsDetail()
  })

  async function loadNewsDetail() {
    try {
      isLoading = true

      if ($selectedNewsPost) {
        post = $selectedNewsPost
        showComingSoonState = false
        markNewsPostAsRead(post.id)
        return
      }

      if (!$selectedNewsPostId) {
        post = null
        return
      }

      post = await getNewsPostDetail($selectedNewsPostId)

      if (post) {
        setSelectedNewsPost(post)
        markNewsPostAsRead(post.id)
        showComingSoonState = false
      }
    } catch (error) {
      post = null
      showComingSoonState = true
      console.error('Cannot load newsfeed detail page', error)
    } finally {
      isLoading = false
    }
  }

  function openImagePreview(imageUrl = '', imageAlt = '') {
    if (!imageUrl) {
      return
    }

    previewImageUrl = imageUrl
    previewImageAlt = imageAlt
  }

  function closeImagePreview() {
    previewImageUrl = ''
    previewImageAlt = ''
  }
</script>

<div class="page flex h-full flex-col overflow-hidden bg-[#EFEFEF]">
  <div class="relative shrink-0">
    <Navbar showBack={true} on:back={() => PubSub.publish('DRIVER.CHANGE_PAGE', { component: PageNewsList })}/>
    <img
      src="{new URL('@/assets/general/loca-logo-horizontal.png', import.meta.url).href}"
      width="100"
      class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      alt="Loca"
    >
  </div>

  <div class="flex-1 overflow-y-auto">
    <div class="px-4 pb-6">
      {#if isLoading}
        <div class="rounded-xl bg-white p-5 shadow-sm animate-pulse">
          <div class="h-6 w-24 rounded-full bg-gray-200"></div>
          <div class="mt-4 h-6 w-3/4 rounded bg-gray-200"></div>
          <div class="mt-3 h-4 w-32 rounded bg-gray-200"></div>
          <div class="mt-6 h-4 w-full rounded bg-gray-200"></div>
          <div class="mt-3 h-4 w-full rounded bg-gray-200"></div>
          <div class="mt-3 h-4 w-5/6 rounded bg-gray-200"></div>
        </div>
      {:else if showComingSoonState}
        <NewsComingSoonState/>
      {:else if !post}
        <NewsEmptyState
          title="ບໍ່ພົບປະກາດ"
          message="ປະກາດນີ້ບໍ່ພ້ອມໃຊ້ງານ ຫຼື ອາດຈະໝົດອາຍຸແລ້ວ"
        />
      {:else}
        <article class="overflow-hidden rounded-2xl bg-white shadow-sm">
          <div class="space-y-4 p-5">
            <AnnouncementBadges {post}/>

            <div class="space-y-3">
              <h1 class="text-2xl font-bold leading-8 text-slate-900">{post.title}</h1>

              <div class="grid gap-2 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                <div><span class="font-semibold text-slate-800">ລົງປະກາດ:</span> {formatNewsDate(post.publishAt)}</div>
                <div><span class="font-semibold text-slate-800">ອັບເດດຫຼ້າສຸດ:</span> {formatNewsDate(post.lastUpdate)}</div>
                {#if post.expireAt}
                  <div><span class="font-semibold text-slate-800">ໝົດອາຍຸ:</span> {formatNewsDate(post.expireAt)}</div>
                {/if}
              </div>
            </div>
          </div>

          {#if post.coverImage}
            <div class="border-y border-slate-100 bg-slate-50 px-3 py-3">
              <div class="overflow-hidden rounded-2xl bg-white">
                <button
                  type="button"
                  class="block w-full"
                  on:click={() => openImagePreview(post.coverImage, post.title)}
                >
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    class="w-full h-auto cursor-zoom-in object-contain"
                  >
                </button>
              </div>
            </div>
          {/if}

          <div class="space-y-4 p-5">
            {#if post.summary}
              <div class="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-blue-dark">
                {post.summary}
              </div>
            {/if}

            <div class="border-l-4 border-blue-primary pl-4 text-sm font-semibold leading-6 text-slate-500">
              ລາຍລະອຽດປະກາດ
            </div>

            <div class="space-y-4 text-base leading-7 text-slate-700">
              {#each getNewsContentParagraphs(post.content) as paragraph}
                <p>{paragraph}</p>
              {/each}
            </div>

            {#if post.ctaLabel && post.ctaLink}
              <button
                type="button"
                class="w-full rounded-2xl bg-blue-dark px-4 py-3.5 text-center font-semibold text-white shadow-sm"
                on:click={() => openDeeplink(post.ctaLink!)}
              >
                {post.ctaLabel}
              </button>
            {/if}
          </div>
        </article>
      {/if}
    </div>
  </div>
</div>

<ImagePreviewModal
  isOpen={Boolean(previewImageUrl)}
  imageUrl={previewImageUrl}
  imageAlt={previewImageAlt}
  onClose={closeImagePreview}
/>
